/**
 * Permission codes the reporting-governance screens declare an interest in
 * (`internal/reporting/enums_c12.go`, design C12 §6.1).
 *
 * Two of these are an **SoD pair** and the screens have to know it, not just the
 * server: `reporting.metric.manage` ⊗ `reporting.metric.certify`. Whoever can
 * change what DSO means must not be the person who certifies that it means the
 * right thing (§6.1). A user holding only the first sees **no certify control at
 * all** — not a disabled one — with a line explaining why, because a disabled
 * button teaches an operator that the system is broken rather than that the
 * control is deliberate (§8.3).
 *
 * `dailyops.publish` is separate from `dailyops.read` for the reason §8.4 gives:
 * a report that publishes itself cannot be checked before anyone is told.
 */

export const REPORTING_PERMISSIONS = {
  /**
   * Reading a dashboard, drilling one of its cards, exporting either, and
   * triggering a rebuild — all four are `reporting.dashboard.read` on the
   * server (`internal/reporting/handler.go`). The rebuild sharing the READ
   * permission is deliberate there: it recomputes a projection from facts the
   * caller may already see and writes no business data, so it is a refresh
   * rather than a mutation of anything.
   */
  dashboardRead: 'reporting.dashboard.read',
  /** Saved filter sets. A set is owned by its creator; sharing one is a flag on it. */
  filterManage: 'reporting.filter.manage',
  metricRead: 'reporting.metric.read',
  metricManage: 'reporting.metric.manage',
  metricCertify: 'reporting.metric.certify',
  outputRead: 'reporting.output.read',
  dailyOpsRead: 'reporting.dailyops.read',
  dailyOpsPublish: 'reporting.dailyops.publish',
  dailyOpsManage: 'reporting.dailyops.manage',
  exceptionViewRead: 'reporting.exceptionview.read',
  exceptionViewManage: 'reporting.exceptionview.manage',
} as const

export type ReportingPermission =
  (typeof REPORTING_PERMISSIONS)[keyof typeof REPORTING_PERMISSIONS]
